import { yupResolver } from '@hookform/resolvers/yup'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import SaveAltIcon from '@mui/icons-material/SaveAlt'
import { Alert, Box, Button, Paper, Stack, Tab, Tabs, Typography } from '@mui/material'
import { usePostImportCsvFile } from '@src/api/import-api'
import TabPanel from '@src/components/Common/Tabs/TabPanel'
import CsvPreviewDataTabe from '@src/components/Import/CsvPreviewDataTabe'
import PageContainer from '@src/components/Layout/PageContainer'
import PageHeader from '@src/components/Layout/PageHeader'
import PageLoader from '@src/components/Loader/PageLoader'
import parseCSV from '@src/services/csvPaser'
import { AxiosError } from 'axios'
import { useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import * as yup from '@if/core/yup'
import { UncontrolledUploadAreaButton } from '@if/mui'
import { ImportParams } from '@src/types/import'

export interface FormValues {
  presenceFile?: FileList
  legacyPresenceFile?: FileList
  remoteExceptionsFile?: FileList
}

interface ErrorResponse {
  message?: string
  type?: string
}

const ImportPage = () => {
  const { t } = useTranslation()
  const [tabIndex, setTabIndex] = useState(0)
  const [importFile, isPending, isSuccess, error] = usePostImportCsvFile()
  const [rows, setRows] = useState<string[][]>([[]])

  const resolver = yupResolver(
    yup.object().shape({
      presenceFile: yup.mixed<FileList>().test('fileType', 'Only CSV files are accepted', (value) => {
        if (!value || value.length === 0) return true
        const file = value[0]
        return file.type === 'text/csv' || file.name.endsWith('.csv')
      }),
      legacyPresenceFile: yup.mixed<FileList>().test('fileType', 'Only CSV files are accepted', (value) => {
        if (!value || value.length === 0) return true
        const file = value[0]
        return file.type === 'text/csv' || file.name.endsWith('.csv')
      }),
      remoteExceptionsFile: yup.mixed<FileList>().test('fileType', 'Only CSV files are accepted', (value) => {
        if (!value || value.length === 0) return true
        const file = value[0]
        return file.type === 'text/csv' || file.name.endsWith('.csv')
      }),
    }),
  )

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    clearErrors,
  } = useForm({
    resolver,
  })

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    let file: File | undefined
    let importType: string | undefined

    if (data.presenceFile?.[0]) {
      file = data.presenceFile[0]
      importType = 'presence'
      clearErrors('presenceFile')
    } else if (data.legacyPresenceFile?.[0]) {
      file = data.legacyPresenceFile[0]
      importType = 'legacy-presence'
      clearErrors('legacyPresenceFile')
    } else if (data.remoteExceptionsFile?.[0]) {
      file = data.remoteExceptionsFile[0]
      importType = 'remote-exceptions'
      clearErrors('remoteExceptionsFile')
    }

    if (file && importType) {
      if (tabIndex == 0) {
        const text = await file.text()
        setRows(parseCSV(text))
        setTabIndex(1)
      } else {
        const params: ImportParams = { importType, file }
        void importFile(params).then(() => {
          setTabIndex(0)
          setRows([[]])
          reset()
        })
      }
    }
  }

  if (isPending) {
    return <PageLoader />
  }
  return (
    <>
      <PageHeader
        title={t('breadcrumbs.import')}
        breadcrumbs={{
          path: [{ href: '/import', label: t('breadcrumbs.import') }],
        }}
      />
      <PageContainer>
        <Box sx={{ width: '90%' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabIndex} aria-label="basic tabs example" variant="fullWidth">
              <Tab label={t('import.fileImport')} onClick={() => setTabIndex(0)} />
              <Tab label={t('import.preview')} onClick={() => setTabIndex(1)} />
            </Tabs>
          </Box>
          <form onSubmit={handleSubmit(onSubmit)}>
            <TabPanel value={tabIndex} index={0}>
              {errors.presenceFile && (
                <Alert icon={<ErrorOutlineIcon />} severity="error">
                  {errors.presenceFile.message}
                </Alert>
              )}
              {errors.legacyPresenceFile && (
                <Alert icon={<ErrorOutlineIcon />} severity="error">
                  {errors.legacyPresenceFile.message}
                </Alert>
              )}
              {errors.remoteExceptionsFile && (
                <Alert icon={<ErrorOutlineIcon />} severity="error">
                  {errors.remoteExceptionsFile.message}
                </Alert>
              )}
              {error && (
                <Alert icon={<ErrorOutlineIcon fontSize="inherit" />} severity="error">
                  {(error as AxiosError<ErrorResponse>).response?.data?.message || 'An error occurred'}
                </Alert>
              )}
              {isSuccess && <Alert severity="success">{t('import.importSuccess')}</Alert>}
              <Box display="flex" alignItems="center" justifyContent="space-between" margin={1}>
                <Paper elevation={3} sx={{ padding: 2, width: '100%' }} variant="outlined">
                  <Stack spacing={1}>
                    <Typography variant="h6"> {t('import.presenceFile')}</Typography>
                    <UncontrolledUploadAreaButton
                      control={control}
                      name="presenceFile"
                      label={t('import.selectFile')}
                      accept=".csv"
                      multiple
                    />
                    <Typography variant="h6"> {t('import.legacyPresenceFile')}</Typography>
                    <UncontrolledUploadAreaButton
                      control={control}
                      name="legacyPresenceFile"
                      label={t('import.selectFile')}
                      accept=".csv"
                      multiple
                    />
                    <Typography variant="h6"> {t('import.approvedRemoteExceptionsFile')}</Typography>
                    <UncontrolledUploadAreaButton
                      control={control}
                      name="remoteExceptionsFile"
                      label={t('import.selectFile')}
                      accept=".csv"
                      multiple
                    />
                  </Stack>
                </Paper>
              </Box>

              <Box display="flex" alignItems="center" justifyContent="end" margin={1}>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<NavigateNextIcon />}
                  type="submit"
                  disabled={Object.keys(errors).length > 0}
                  loading={isPending}
                >
                  {t('common.next')}
                </Button>
              </Box>
            </TabPanel>
            <TabPanel value={tabIndex} index={1}>
              <CsvPreviewDataTabe data={rows} />

              <Box display="flex" alignItems="center" justifyContent="end" margin={1}>
                <Button type="submit" variant="contained" color="secondary" startIcon={<SaveAltIcon />}>
                  {t('common.confirm')}
                </Button>
              </Box>
            </TabPanel>
          </form>
        </Box>
      </PageContainer>
    </>
  )
}
export default ImportPage
